import React from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/blocks/AnimatedSection';
import { getCortexEventBySlug } from '../../config/cortexEvents';
import { themeColors, themeSemantic } from '../../config/themeColors';

const ESSAY_SLUG = 'essay-competition-2025';

/** Hover colour per theme (amber, emerald, purple, blue) */
const THEME_CARD_HOVERS = [themeColors.amber.cardHover, themeColors.emerald.cardHover, themeColors.purple.cardHover, themeColors.blue.cardHover];

const EssayCompetition2025 = () => {
    const event = getCortexEventBySlug(ESSAY_SLUG);
    const details = event?.details ?? [];
    const divisions = details.filter((d) => d.label.startsWith('Division'));
    const themeDetails = details.filter((d) => d.label.startsWith('Theme'));
    const divisionsList = divisions.length > 0 ? divisions : [{ label: 'Division 1 (D1)', value: 'F4–F6 students' }, { label: 'Division 2 (D2)', value: 'Tertiary level and above' }];
    const themes = themeDetails.length > 0 ? themeDetails.map((d) => d.value) : ['Human Identity & Experience', 'Global Citizenry', 'Artificial Intelligence', 'Sustainability'];

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    {event?.posterImage && (
                        <div className="mb-8 flex justify-center">
                            <img
                                src={event.posterImage}
                                alt={event.posterAlt || 'CORTEX Essay Competition 2025 Poster'}
                                className="w-auto h-auto max-w-md md:max-w-lg object-contain rounded-lg shadow-lg"
                            />
                        </div>
                    )}
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-8 mb-4">CORTEX Essay Competition 2025</h1>
                    <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-6">
                        Share your unique perspectives on transdisciplinarity
                    </p>
                    <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-4">
                        {event?.posterSubtitle || 'Submission deadline: 31 December 2025'}
                    </p>
                    <p className="inline-block px-4 py-2 rounded-full bg-gray-200 text-gray-700 font-semibold text-sm">
                        Submission closed
                    </p>
                </div>

                {/* Overview Section */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Overview</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed text-justify mb-4">
                            We all take common core courses and study general education; yet what exactly is transdisciplinarity? Join our essay competition, and share us your unique perspectives on how transdisciplinarity is involved in your areas of interest.
                        </p>
                    </div>
                </AnimatedSection>

                {/* Divisions */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Divisions</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed text-justify mb-6">
                            The essay competition is divided into two divisions:
                        </p>
                        <div className="grid md:grid-cols-2 gap-6">
                            {divisionsList.map((div, i) => (
                                <div
                                    key={div.label}
                                    className={`p-6 border-l-4 rounded-lg ${i === 0 ? themeColors.amber.cardHover : themeColors.emerald.cardHover} ${i === 0 ? themeColors.amber.border : themeColors.emerald.border}`}
                                >
                                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{div.label}</h3>
                                    <p className="text-gray-700">{div.value}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </AnimatedSection>

                {/* Themes */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Themes</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed text-justify mb-6">
                            One essay question is provided for each division, exploring one of the four themes below. You are free to choose any one of these themes for your essay.
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                            {themes.map((theme, i) => (
                                <div key={theme} className={`p-4 rounded-lg ${THEME_CARD_HOVERS[i % THEME_CARD_HOVERS.length]}`}>
                                    <p className="text-gray-700 font-medium">{i + 1}. {theme}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </AnimatedSection>

                {/* Essay Questions by Division */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Essay Questions by Division</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="space-y-8">
                            <div className={`p-6 border-l-4 ${themeColors.amber.border} rounded-lg ${themeColors.amber.bgMuted} ${themeColors.amber.cardHover}`}>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">D1 (F4–F6)</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    Briefly describe your understanding of transdisciplinary collaboration. Select one of the above themes and elaborate on the benefits of transdisciplinary collaboration.
                                </p>
                                <div className={`mt-4 p-3 bg-white rounded border ${themeColors.amber.borderThin}`}>
                                    <p className="text-gray-700 font-medium">Word count: 500 words expected, 700 words maximum</p>
                                </div>
                            </div>
                            <div className={`p-6 border-l-4 ${themeColors.emerald.border} rounded-lg ${themeColors.emerald.bgMuted} ${themeColors.emerald.cardHover}`}>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">D2 (Tertiary and above)</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    What is transdisciplinarity? Critically evaluate what transdisciplinarity represents in any one of the above themes — detail also the theme-specific challenges to transdisciplinary cooperation and potential solutions.
                                </p>
                                <div className={`mt-4 p-3 bg-white rounded border ${themeColors.emerald.borderThin}`}>
                                    <p className="text-gray-700 font-medium">Word count: 1200 words expected, 1500 words maximum</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </AnimatedSection>

                {/* Rules */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Rules</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">•</span>
                                <p className="text-gray-700">No plagiarism and AI-generated content</p>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">•</span>
                                <p className="text-gray-700">No double submission</p>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">•</span>
                                <p className="text-gray-700">Academic language</p>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">•</span>
                                <p className="text-gray-700">Empirical data collection is not permitted unless prior approval has been obtained through the Ethics Committee (will require participants to declare)</p>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">•</span>
                                <p className="text-gray-700">All citations in APA</p>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">•</span>
                                <p className="text-gray-700">Font size 12, Times New Roman, 1.5 spacing</p>
                            </li>
                        </ul>
                    </div>
                </AnimatedSection>

                {/* Submission */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Submission</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className={`mb-3 p-4 rounded-lg border-2 border-gray-300 bg-gray-50 text-center`}>
                            <p className="text-lg font-semibold text-gray-800">Submissions have closed.</p>
                            <p className="text-gray-600 mt-1">The submission deadline was 31 December 2025. Thank you for your interest.</p>
                        </div>
                    </div>
                </AnimatedSection>

                {/* Prizes */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Prizes</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className={`p-6 border-l-4 ${themeColors.amber.border} rounded-lg ${themeColors.amber.cardHover}`}>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Publication in JELTR</h3>
                            <p className="text-gray-700 mb-3">Winners will have the opportunity to have their work published in the Journal for Experiential Learning and Transdisciplinary Research (JELTR).</p>
                            <Link
                                to="/jeltr"
                                className={`inline-block px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold transition ${themeSemantic.jeltr.buttonHover}`}
                            >
                                Learn more about JELTR →
                            </Link>
                        </div>
                    </div>
                </AnimatedSection>

                {/* Organised by CORTEX */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Organised by CORTEX</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed text-justify">
                            CORTEX (Conference for Research and Transdisciplinary Experiential Learning) is a student-led platform under the Co-curricular Support Office (CCSO) at the University of Hong Kong. We are dedicated to providing undergraduate students in Hong Kong with opportunities to experience academia through hosting academic conferences and competitions.
                        </p>
                        <div className="mt-6">
                            <Link
                                to="/cortex"
                                className={`inline-block px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold transition ${themeSemantic.cortex.buttonHover}`}
                            >
                                Learn More About CORTEX →
                            </Link>
                        </div>
                    </div>
                </AnimatedSection>
            </div>
        </div>
    );
};

export default EssayCompetition2025;
